// src/pages/notifications/UserPreferencesPage.tsx
// User notification preferences: global settings, quiet hours, channels, categories.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useNotificationsSetting, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { FormRow } from '@/components/form';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { formatChannelType, formatCategory, daysOfWeek } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { NotificationCategoryValues } from '@core/types/notifications/NotificationCategory';
import type { UserGlobalNotificationSettings } from '@core/types/notifications/UserGlobalNotificationSettings';
import type { EventNotificationRule } from '@core/types/notifications/EventNotificationRule';
import { EventRulesEditor } from '../../features/notifications/EventRulesEditor';

export const UserPreferencesPage = observer(() => {
  const store = useNotificationsSetting();
  const { t } = useI18n();
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [eventRules, setEventRules] = useState<EventNotificationRule[]>([]);
  const [isSavingEventRules, setIsSavingEventRules] = useState(false);
  const [eventRulesSaved, setEventRulesSaved] = useState(false);

  const [form, setForm] = useState({
    notificationsEnabled: true,
    timezone: 'UTC',
    preferredLanguage: 'en',
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
    quietHoursDays: [0, 1, 2, 3, 4, 5, 6] as number[],
    preferredChannels: [] as string[],
    categoryPreferences: {} as Record<string, { enabled: boolean; channels: string[] }>,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await store.GetUserSettingsGlobal('me');
        const s = resp as UserGlobalNotificationSettings | null;
        if (s && !loaded) {
          setForm({
            notificationsEnabled: s.notifications_enabled ?? true,
            timezone: s.timezone ?? 'UTC',
            preferredLanguage: s.preferred_language ?? 'en',
            quietHoursEnabled: s.quiet_hours?.enabled ?? false,
            quietHoursStart: s.quiet_hours?.start_time ?? '22:00',
            quietHoursEnd: s.quiet_hours?.end_time ?? '08:00',
            quietHoursDays: s.quiet_hours?.days_of_week ?? [0, 1, 2, 3, 4, 5, 6],
            preferredChannels: (s.preferred_channels as unknown as { items?: string[] })?.items ?? [],
            categoryPreferences: {},
          });
          setLoaded(true);
        }
      } catch {
        // No settings row yet is the normal first-load state, not a failure —
        // clear the store error so the page does not show an error banner.
        store.clearError();
        setLoaded(true);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadEventRules = async () => {
      try {
        const resp = await store.GetUserSettingsEvent('me');
        setEventRules(resp.settings?.event_rules?.items ?? []);
      } catch {
        // No event-specific settings row yet — same normal first-load state
        // as the global settings load above.
        store.clearError();
      }
    };
    loadEventRules();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveEventRules = async () => {
    setIsSavingEventRules(true);
    try {
      await store.UpdateUserSettingsEvent('me', {
        settings: { event_rules: { items: eventRules } },
      } as never);
      setEventRulesSaved(true);
      setTimeout(() => setEventRulesSaved(false), 3000);
    } catch {
      // handled by store
    } finally {
      setIsSavingEventRules(false);
    }
  };

  const toggleChannel = (ch: string) => {
    setForm((p) => ({
      ...p,
      preferredChannels: p.preferredChannels.includes(ch)
        ? p.preferredChannels.filter((c) => c !== ch)
        : [...p.preferredChannels, ch],
    }));
  };

  const toggleDay = (day: number) => {
    setForm((p) => ({
      ...p,
      quietHoursDays: p.quietHoursDays.includes(day)
        ? p.quietHoursDays.filter((d) => d !== day)
        : [...p.quietHoursDays, day].sort(),
    }));
  };

  const handleSave = async () => {
    try {
      await store.UpdateUserSettingsGlobal('me', {
        notifications_enabled: form.notificationsEnabled,
        timezone: form.timezone,
        preferred_language: form.preferredLanguage,
        quiet_hours: {
          enabled: form.quietHoursEnabled,
          start_time: form.quietHoursStart,
          end_time: form.quietHoursEnd,
          days_of_week: form.quietHoursDays,
        },
        preferred_channels: { items: form.preferredChannels },
      } as never);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // handled by store
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('notifications.userPreferences')}</h2>
        <p className="text-sm text-muted-foreground">{t('notifications.userPreferencesDesc')}</p>
      </div>

      {saved && <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>{t('notifications.saveSuccess')}</Alert>}
      {store.error ? <ErrorAlert error={store.error} /> : null}

      <Card>
        <CardHeader><CardTitle>{t('notifications.generalSettings')}</CardTitle></CardHeader>
        <CardContent>
          <FormRow label={t('notifications.notificationsEnabled')}>
            <ToggleSwitch label="" description={t('notifications.notificationsEnabledDesc')} checked={form.notificationsEnabled} onChange={(v) => setForm((p) => ({ ...p, notificationsEnabled: v }))} />
          </FormRow>
          <FormRow label={t('notifications.defaultTimezone')} htmlFor="up-timezone">
            <input id="up-timezone" value={form.timezone} onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))} autoCapitalize="off" className="w-40 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </FormRow>
          <FormRow label={t('notifications.defaultLanguage')} htmlFor="up-language">
            <input id="up-language" value={form.preferredLanguage} onChange={(e) => setForm((p) => ({ ...p, preferredLanguage: e.target.value }))} autoCapitalize="off" className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </FormRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t('notifications.quietHours')}</CardTitle></CardHeader>
        <CardContent>
          <FormRow label={t('notifications.quietHoursEnabled')}>
            <ToggleSwitch label="" description={t('notifications.quietHoursDesc')} checked={form.quietHoursEnabled} onChange={(v) => setForm((p) => ({ ...p, quietHoursEnabled: v }))} />
          </FormRow>
          {form.quietHoursEnabled && (
            <>
              <FormRow label={t('notifications.quietHoursStart')} htmlFor="up-quiet-start">
                <input id="up-quiet-start" type="time" value={form.quietHoursStart} onChange={(e) => setForm((p) => ({ ...p, quietHoursStart: e.target.value }))} className="rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('notifications.quietHoursEnd')} htmlFor="up-quiet-end">
                <input id="up-quiet-end" type="time" value={form.quietHoursEnd} onChange={(e) => setForm((p) => ({ ...p, quietHoursEnd: e.target.value }))} className="rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring" />
              </FormRow>
              <FormRow label={t('notifications.quietHoursDays')}>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map((day) => (
                    <button key={day.value} type="button" onClick={() => toggleDay(day.value)}
                      className={`px-3 py-1 rounded-lg text-sm border ${form.quietHoursDays.includes(day.value) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border'}`}>
                      {t(day.labelKey)}
                    </button>
                  ))}
                </div>
              </FormRow>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t('notifications.preferredChannels')}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{t('notifications.preferredChannelsDesc')}</p>
          <div className="space-y-3">
            {NotificationChannelTypeValues.filter(v => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map((ch) => {
              const ct = formatChannelType(ch);
              return (
                <div key={ch} className="flex items-center gap-2">
                  <NotificationIcon name={ct.icon} className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <ToggleSwitch label={t(ct.labelKey)} checked={form.preferredChannels.includes(ch)} onChange={() => toggleChannel(ch)} />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t('notifications.categoryPreferences')}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">{t('notifications.categoryPreferencesDesc')}</p>
          <div className="space-y-3">
            {NotificationCategoryValues.filter(v => v !== 'NOTIFICATION_CATEGORY_UNSPECIFIED').map((cat) => {
              const c = formatCategory(cat);
              return (
                <div key={cat} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm font-medium">{t(c.labelKey)}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} isLoading={store.isLoading}>{t('common.save')}</Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.eventRules.title')}</CardTitle>
          <CardDescription>{t('notifications.eventRules.userDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {eventRulesSaved && <Alert variant="success" className="mb-4">{t('notifications.saveSuccess')}</Alert>}
          <EventRulesEditor rules={eventRules} onChange={setEventRules} />
        </CardContent>
        <CardFooter>
          <Button onClick={handleSaveEventRules} isLoading={isSavingEventRules}>{t('common.save')}</Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default UserPreferencesPage;
